import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Grid,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import SettingsIcon from "@mui/icons-material/Settings";
import { hubApi } from "../../services/hubApi";

const providers = [
  { value: "gmail", label: "Gmail", hint: "Use Senha de App" },
  { value: "outlook", label: "Outlook / Hotmail", hint: "" },
  { value: "yahoo", label: "Yahoo Mail", hint: "" },
  { value: "custom", label: "Personalizado", hint: "Configurar IMAP/SMTP manualmente" },
];

export default function EmailConnect({ onBack, onSuccess }) {
  const [emailUser, setEmailUser] = useState("");
  const [emailPass, setEmailPass] = useState("");
  const [provider, setProvider] = useState("gmail");
  const [connName, setConnName] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advanced, setAdvanced] = useState({
    imapHost: "",
    imapPort: 993,
    imapTls: true,
    smtpHost: "",
    smtpPort: 587,
    smtpSecure: false,
    emailFrom: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const isCustom = provider === "custom";

  const handleConnect = async () => {
    if (!emailUser || !emailPass) return;

    setError("");
    setLoading(true);

    try {
      const params = {
        emailUser: emailUser.trim(),
        emailPass,
        provider: isCustom ? undefined : provider,
        name: connName || undefined,
        ...(isCustom || showAdvanced
          ? {
              imapHost: advanced.imapHost,
              imapPort: advanced.imapPort,
              imapTls: advanced.imapTls,
              smtpHost: advanced.smtpHost,
              smtpPort: advanced.smtpPort,
              smtpSecure: advanced.smtpSecure,
              emailFrom: advanced.emailFrom || undefined,
            }
          : {}),
      };

      const { data } = await hubApi.emailConnect(params);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao conectar email.");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CheckCircleIcon sx={{ fontSize: 72, color: "success.main", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Email conectado!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {result.email}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Polling IMAP ativo — novos emails serao recebidos automaticamente
        </Typography>
        <Button
          variant="contained"
          onClick={onSuccess}
          sx={{
            mt: 3,
            background: "linear-gradient(135deg, #6B48FF 0%, #8B6FFF 100%)",
          }}
        >
          Concluir
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 3, color: "text.secondary" }}
      >
        Voltar
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Provedor</InputLabel>
            <Select
              value={provider}
              label="Provedor"
              onChange={(e) => setProvider(e.target.value)}
            >
              {providers.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                  {p.hint && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 1, color: "text.secondary" }}
                    >
                      ({p.hint})
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            placeholder="seu@email.com"
            value={emailUser}
            onChange={(e) => setEmailUser(e.target.value)}
            type="email"
            autoComplete="off"
            inputProps={{ autoComplete: "new-password" }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Senha"
            placeholder={
              provider === "gmail"
                ? "Senha de App (16 caracteres)"
                : "Senha do email"
            }
            value={emailPass}
            onChange={(e) => setEmailPass(e.target.value)}
            type="password"
            autoComplete="new-password"
            inputProps={{ autoComplete: "new-password" }}
          />
        </Grid>

        {provider === "gmail" && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
              Para Gmail, use uma Senha de App. Acesse: Conta Google &gt; Seguranca
              &gt; Senhas de App. A verificacao em duas etapas deve estar ativada.
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Nome da Conexao (opcional)"
            placeholder="Email Suporte"
            value={connName}
            onChange={(e) => setConnName(e.target.value)}
          />
        </Grid>

        {/* Configuracoes avancadas */}
        <Grid item xs={12}>
          <Button
            startIcon={<SettingsIcon />}
            onClick={() => setShowAdvanced(!showAdvanced)}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            {showAdvanced ? "Ocultar" : "Mostrar"} configuracoes avancadas
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Collapse in={showAdvanced || isCustom}>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  IMAP (Recebimento)
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Host IMAP"
                  placeholder="imap.gmail.com"
                  value={advanced.imapHost}
                  onChange={(e) =>
                    setAdvanced({ ...advanced, imapHost: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Porta"
                  type="number"
                  value={advanced.imapPort}
                  onChange={(e) =>
                    setAdvanced({ ...advanced, imapPort: Number(e.target.value) })
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={advanced.imapTls}
                      onChange={(e) =>
                        setAdvanced({ ...advanced, imapTls: e.target.checked })
                      }
                      size="small"
                    />
                  }
                  label="TLS"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  SMTP (Envio)
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Host SMTP"
                  placeholder="smtp.gmail.com"
                  value={advanced.smtpHost}
                  onChange={(e) =>
                    setAdvanced({ ...advanced, smtpHost: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Porta"
                  type="number"
                  value={advanced.smtpPort}
                  onChange={(e) =>
                    setAdvanced({ ...advanced, smtpPort: Number(e.target.value) })
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={advanced.smtpSecure}
                      onChange={(e) =>
                        setAdvanced({ ...advanced, smtpSecure: e.target.checked })
                      }
                      size="small"
                    />
                  }
                  label="SSL"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email Remetente (From)"
                  placeholder="Mesmo do login se vazio"
                  value={advanced.emailFrom}
                  onChange={(e) =>
                    setAdvanced({ ...advanced, emailFrom: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </Collapse>
        </Grid>

        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleConnect}
            disabled={loading || !emailUser || !emailPass}
            sx={{
              mt: 1,
              py: 1.5,
              background: "linear-gradient(135deg, #FF6B00 0%, #CC5500 100%)",
              "&:hover": { opacity: 0.9 },
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={22} sx={{ color: "#fff" }} />
                <span>Testando conexao...</span>
              </Box>
            ) : (
              "Conectar Email"
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
